import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/websiteBuilder";
import { TWebsiteBuilderTreeRefMap } from "pages/secure/facility/websiteBuilder/editor/hooks/treeRefMap/useTreeRefMapRegistration";
import {
  TWebsiteBuilderElement,
  TWebsiteBuilderElementData,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { ButtonSettings } from "./settings/button/ButtonSettings";
import { DividerSettings } from "./settings/divider/DividerSettings";
import { InputSettings } from "./settings/input/InputSettings";
import { ParagraphSettings } from "./settings/paragraph/ParagraphSettings";
import { IWebsiteBuilderPopupContent, WebsiteBuilderPopup } from "../../helpers/popup/WebsiteBuilderPopup";
import { SelectSettings } from "./settings/select/SelectSettings";
import { HeadingSettings } from "./settings/heading/HeadingSettings";

export interface IUseElementViewSettingsArgs {
  id: string;
  parentId: string;
  selected: boolean;
  settingsActive: boolean;
  node: TWebsiteBuilderElement;
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

interface IUseElementViewSettings {
  viewSettings: JSX.Element;
  handleViewSettingsDoubleClick: () => void;
}

export function useElementViewSettings(args: IUseElementViewSettingsArgs): IUseElementViewSettings {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const element: TWebsiteBuilderElementData = args.node.element;

  const [domRect, setDomRect] = useState<DOMRect>(null);

  useEffect(() => {
    if (!args.settingsActive) {
      setDomRect(null);
    } else {
      const domRect = args.treeRefMap.current.get(args.id)?.getBoundingClientRect();

      setDomRect(domRect);
    }
  }, [args.settingsActive, args.id]);

  const popupContent: IWebsiteBuilderPopupContent = useMemo(() => {
    if (element.type === "button") {
      return {
        heading: t("Button style"),
        render: () => <ButtonSettings id={args.id} settings={element.settings} />,
      };
    } else if (element.type === "divider") {
      return {
        heading: t("Divider settings"),
        render: () => <DividerSettings id={args.id} settings={element.settings} />,
      };
    } else if (element.type === "input") {
      return {
        heading: t("Input field style"),
        render: () => <InputSettings id={args.id} settings={element.settings} />,
      };
    } else if (element.type === "paragraph") {
      return {
        heading: t("Text style"),
        render: () => <ParagraphSettings />,
      };
    } else if (element.type === "select") {
      return {
        heading: t("Dropdown settings"),
        render: () => <SelectSettings id={args.id} settings={element.settings} />,
      };
    } else if (element.type === "heading") {
      return {
        heading: t("Heading style"),
        render: () => <HeadingSettings id={args.id} settings={element.settings} />,
      };
    } else {
      return null;
    }
  }, [element.type, args.id, element.settings]);

  function handleViewSettingsDoubleClick() {
    if (!args.selected) {
      return;
    }

    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          editingMode: args.settingsActive ? "none" : "element-settings",
        },
      }),
    );
  }

  function closeElementViewPopup() {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          editingMode: "none",
        },
      }),
    );
  }

  function renderViewSettings() {
    if (!args.settingsActive) {
      return null;
    } else {
      return (
        <WebsiteBuilderPopup
          domRect={domRect}
          popupContent={popupContent}
          treeRefMap={args.treeRefMap}
          closePopup={closeElementViewPopup}
        />
      );
    }
  }

  return {
    viewSettings: renderViewSettings(),
    handleViewSettingsDoubleClick,
  };
}
